import { $, _ } from "js/vendor";
import { parse, Font } from "opentype.js";
import { getStaticUrl } from "js/config";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import { calculateFallbackRangeStart, allFonts, fallbackRanges, bodyFonts, titleFonts } from "common/font_constants.js";
import log from "../logger";
import { TextStyleEnum } from "common/constants";
import { timeoutRace } from "js/core/utilities/promiseHelper";
import { downloadFromBlob } from "js/core/utilities/utilities";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import fallbackTTF from "arraybuffer-loader!./fallback.ttf";

app.glyphCache = {};
app.wordWidthCache = {};

window.generateFontImages = () => {
    [].concat(titleFonts, bodyFonts).forEach((font, i) => {
        setTimeout(() => {
            app.fontManager.downloadFont(font.font, window.staticUrl + `/fonts/${font.url}`, font.font)
                .then(() => {
                    app.fontManager.saveTextImage(app.fontManager.fontCache[font.font], font.label, undefined, font);
                });
        }, i * 500);
    });
};

function assembleFallbackFontPath(fallbackData, weight) {
    let path, name;
    if (fallbackData.isCJK) {
        path = fallbackData.path[app.currentTheme.get("cjkFont") || "jp"];
    } else {
        path = fallbackData.path;
    }
    if (weight.equalsAnyOf(TextStyleEnum.BOLD, TextStyleEnum.BOLDITALIC) && fallbackData.hasBold) {
        path = path + "-Bold";
        name = fallbackData.name + "-Bold";
    } else {
        path = path + "-Regular";
        name = fallbackData.name + "-Regular";
    }
    path = path + (fallbackData.isCJK ? ".otf" : ".ttf");
    return { path, name };
}

const NOTO_H_HEIGHT = 71.4;

class FontManager {
    constructor() {
        this.fontCache = {};
        this.fontHeights = {};
    }

    get $cssStyles() {
        if (!this._$cssStyles) {
            this._$cssStyles = $("head").addEl($("<style></style>"));
        }
        return this._$cssStyles;
    }

    get fallbackFont() {
        if (!this._fallbackFont) {
            this._fallbackFont = parse(fallbackTTF);
            this._fallbackFont.isCapsOnly = false;
            this._fallbackFont.isFallback = true;
            this._fallbackFont.name = "Fallback";
            app.glyphCache["Fallback"] = {};
        }
        return this._fallbackFont;
    }

    async loadMeasureFont() {
        if (!this.isMeasureFontLoaded) {
            return new Promise(resolve => {
                this.isMeasureFontLoaded = true;
                this.$cssStyles.append(`@font-face { font-family: "MeasureFont"; src: url('${getStaticUrl("/fonts/measure.otf")}'); }`);
                $("body").append($.div("", "font").css("font-family", "MeasureFont").opacity(0));
                _.delay(() => resolve(), 400);
            });
        } else {
            return Promise.resolve();
        }
    }

    isFontSystemInstalled(font) {
        let ctx = document.createElement("canvas").getContext("2d");
        ctx.font = `200px ${font.names.preferredFamily ? font.names.preferredFamily.en : font.names.fontFamily.en}, MeasureFont`;
        let width = ctx.measureText("A").width;
        return width > 10;
    }

    isCapsOnly(fontName) {
        return ["BebasNeue"].contains(fontName);
    }

    drawTextToBlob(font, text, { maxWidth = 400, height = 48, fontSize = 32, x = 0, y = 32, color = "black" } = {}) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const measuredWidth = font.getAdvanceWidth(text, fontSize);
        canvas.width = Math.min(measuredWidth, maxWidth);
        canvas.height = height;
        const path = font.getPath(text, x, y, fontSize);
        path.fill = color;
        path.draw(ctx);

        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("No blob created by drawTextToBlob."));
                }
            });
        });
    }

    saveTextImage(font, text, options) {
        this.drawTextToBlob(font, text, options).then(blob => {
            downloadFromBlob(blob, text + ".png");
        });
    }

    loadFontsForTheme(theme, textToLoad) {
        textToLoad = "a“”" + textToLoad;
        const codePoints = textToLoad.split("").map(chr => chr.codePointAt(0));
        let startRanges = [...new Set(codePoints.map(codePoint => String.fromCodePoint(codePoint)))];
        let promises = [];
        let counter = 0;
        let loadSS = (ss, path) => {
            if (counter++ > 10000) {
                return;
            }
            if (!ss) return;
            if (ss["font"]) {
                startRanges.map(chr => {
                    promises.push(this.loadUnicodeFont(ss["font"], chr, TextStyleEnum.REGULAR));
                });
            }
            if (ss["emphasizedFont"]) {
                startRanges.map(chr => {
                    promises.push(this.loadUnicodeFont(ss["emphasizedFont"], chr, TextStyleEnum.BOLD));
                });
            }
            if (ss["italicFont"]) {
                startRanges.map(chr => {
                    promises.push(this.loadUnicodeFont(ss["italicFont"], chr, TextStyleEnum.BOLD));
                });
            }
            if (ss["emphasizedItalicFont"]) {
                startRanges.map(chr => {
                    promises.push(this.loadUnicodeFont(ss["emphasizedItalicFont"], chr, TextStyleEnum.BOLD));
                });
            }
            Object.keys(ss).map(key => {
                if (ss[key] === undefined || ss[key] === null) {
                    log.error(`Stylesheet key '${key}' is undefined. Path is ${path}`);
                } else if (ss[key].constructor === Object) {
                    loadSS(ss[key], `${path} > ${key}`);
                }
            });
        };
        loadSS(theme && theme.styleSheet, "Root");
        return Promise.all(promises.filter(p => p.then));
    }

    waitForFontsToLoad() {
        return timeoutRace(30 * 1000, Promise.all(Object.values(this.fontCache).filter(val => val.then)));
    }

    downloadFont(cacheName, fontUrl, fontName) {
        return fetch(fontUrl).then(res => res.arrayBuffer()).then(arrayBuffer => {
            const font = parse(arrayBuffer);
            this.fontCache[cacheName] = font;
            font.isCapsOnly = this.isCapsOnly(fontName);
            font.isCustomFont = cacheName.indexOf("-") === 0;
            font.name = fontName;

            font.cachedMetrics = font.charToGlyph("H").getMetrics();

            font.wordWidthCache = {};

            font.getWordWidth = (text, fontSize) => {
                if (!font.wordWidthCache.hasOwnProperty(text)) {
                    font.wordWidthCache[text] = font.getAdvanceWidth(text, 100);
                }
                return font.wordWidthCache[text] * (fontSize / 100);
            };

            // add to the glyph cache
            app.glyphCache[font.name] = {};

            this.$cssStyles.append(`@font-face { font-family: "${fontName}"; src: url('${fontUrl}'); }`);

            const hGlyph = font.charToGlyph("H");
            const hGlyphPath = hGlyph.getPath(0, 0, 100);
            let fontHeight;
            if (hGlyphPath.commands.length) {
                let minPt = _.minBy(hGlyphPath.commands, pt => pt.y).y;
                let maxPt = _.maxBy(hGlyphPath.commands, pt => pt.y).y;
                fontHeight = maxPt - minPt;
            } else {
                fontHeight = NOTO_H_HEIGHT;
            }
            this.fontHeights[fontName] = fontHeight;

            if (this.hasNaNWidthGlyphs(font)) {
                return app.dialogManager.showWarning("Text rendering may look incorrect.", `Your font ${fontName} may be corrupted, and text rendering may not look correct. Try another font if you see problems.`);
            }

            font.fontHeight = this.fontHeights[fontName];
        });
    }

    hasNaNWidthGlyphs(font) {
        return Object.values(font.glyphs.glyphs).some(glyph => Number.isNaN(glyph.advanceWidth));
    }

    loadUnicodeFont(fontName, chr, weight) {
        if (!weight) console.warn("No weight (regular or bold) passed, this may cause fallback fonts to load incorrectly.");

        const isCustomFont = fontName.indexOf("-") === 0; // assumes fontName starting with - is a firebase id, revisit this
        const fontPath = allFonts[fontName];

        if (!isCustomFont && !fontPath) { // not custom font and not in font manifest, throw error
            log.error("Using fallback font, no font:", { fontName, isCustomFont });
            return this.fallbackFont;
        } else if (!this.fontCache[fontName]) {
            // no font exists:
            if (isCustomFont) {
                const [fontAssetId, key] = fontName.split("/");
                this.fontCache[fontName] = ds.assets.getAssetById(fontAssetId, "font").then(asset => {
                    return asset.getBaseUrl(key)
                        .then(url => this.downloadFont(fontName, url, `${asset.get("name")}-${key}`))
                        .catch(err => {
                            log.error(err);
                            // attempt to cache bust
                            return asset.getBaseUrl(key, true)
                                .then(url => this.downloadFont(fontName, url, `${asset.get("name")}-${key}`));
                        });
                });
            } else {
                this.fontCache[fontName] = this.downloadFont(fontName, getStaticUrl(`/fonts/${fontPath}`), fontName);
            }
            return this.fontCache[fontName];
        } else if (this.fontCache[fontName].then) {
            // do nothing and return promise if font is already loading
            return this.fontCache[fontName];
        } else if (this.fontCache[fontName] instanceof Font) {
            const font = this.fontCache[fontName];
            const glyph = font.charToGlyph(chr);
            if (glyph.index === 0 || glyph.name === ".notdef") {
                // figure out which fallback font to use for this glpyh
                const codePoint = chr.codePointAt(0);
                const rangeStart = calculateFallbackRangeStart(codePoint);
                const start = rangeStart.toUpperCase();
                const fallbackData = fallbackRanges[start];
                if (!fallbackData) {
                    return this.fallbackFont;
                }
                const { name, path } = assembleFallbackFontPath(fallbackData, weight);
                if (!this.fontCache[path]) {
                    this.fontCache[path] = this.downloadFont(path, getStaticUrl(`/fonts/${path}`), name);
                }
                return this.fontCache[path];
            }
        }
        // font is cached and we can use it directly
        return this.fontCache[fontName];
    }

    exportFont(fontName) {
        fetch(getStaticUrl(`/fonts/FontPackages/${fontName}.zip`))
            .then(resp => resp.blob())
            .then(blob => {
                let url = window.URL.createObjectURL(blob);
                let a = document.createElement("a");
                a.style.display = "none";
                a.download = `${fontName}.zip`;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            }).catch(() => {
                ShowErrorDialog({
                    error: "Unable to download font package",
                    message: "An error occurred while downloading the font package. Please contact Beautiful.ai" +
                    " support for more assistance"
                });
            });
    }
}

const FontSets = [
    {
        id: 1, //old clean
        titleFont: "Source Sans Pro",
        bodyFont: "Source Sans Pro",
        displayFont: "Source Sans Pro",
    },
    {
        id: 2, //old bold
        titleFont: "BebasNeue",
        bodyFont: "Source Sans Pro",
        displayFont: "BebasNeue",
    },
    {
        id: 3, //old classic
        titleFont: "Roboto Slab",
        bodyFont: "Roboto",
        displayFont: "Roboto Slab",
    },
    {
        id: 4, //old sketchy
        titleFont: "BebasNeue",
        bodyFont: "Roboto Slab",
        displayFont: "BebasNeue",
    },
    {
        id: 5, //new default
        titleFont: "Roboto",
        bodyFont: "Roboto",
        displayFont: "Roboto",
    },
    {
        id: 6,
        titleFont: "Playfair Display",
        bodyFont: "Raleway",
        displayFont: "Playfair Display",
    },
    {
        id: 7,
        titleFont: "Montserrat",
        bodyFont: "Montserrat",
        displayFont: "Montserrat",
    }
];

app.fontManager = new FontManager();

export { FontManager, FontSets };
